import React, { useState, useEffect } from "react";
import styled from "styled-components";
import ContentPopup from "../organisms/OrganismsContentPopup";



// --- Monthly Calendar Styles ---------------------------------------------------
const MonthlyCalendarContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const WeekdayHeader = styled.div`
  height: 50px;
  display: flex;
  justify-content: space-around;
  padding: 8px 0;
  background: #F2F2F7;
  align-items: center;
`;

const WeekArea = styled.div`
  width: 248px;
  height: 50px;
  display: flex;
  justify-content: space-around;
  border-right: 1px solid #D1D1D6;
  border-left: 1px solid #D1D1D6;
  align-items: center;
`;

const Weekday = styled.span`
  flex: 1;
  text-align: center;
  color: #666666;
  font-size: 16px;
  font-weight: 400;
`;

const WeekRow = styled.div`
  display: flex;
`;

const DayCell = styled.div`
  flex: 1;
  height: 320px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 8px;
  border: 1px solid #D1D1D6;
  cursor: ${props => props.clickable ? 'pointer' : 'default'};
  
  &:hover {
    background: ${props => props.clickable ? '#f8f9fa' : 'transparent'};
  }
`;

const DayInner = styled.div`
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
`;

const DisabledInner = styled(DayInner)`
  color: #AEAEB2;
`;

const ActiveInner = styled(DayInner)`
  background: #256EF4;
`;

const DayNumber = styled.span`
  font-size: 15px;
  font-family: Pretendard;
  font-weight: 400;
  line-height: 20px;
`;

const DisabledNumber = styled(DayNumber)`
  color: rgba(50, 50, 50, 0.5);
`;

const ActiveNumber = styled(DayNumber)`
  color: white;
  font-weight: 700;
`;

// 콘텐츠 표시를 위한 스타일 컴포넌트들
const ContentList = styled.div`
  width: 100%;
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 5px;
  max-height: 280px;
  overflow-y: auto;
`;

const ContentItem = styled.div`
  width: 100%;
  padding: 7px 10px;
  border-radius: 5px;
  background: ${props => {
    if (props.isPast) return '#F7F7FA';
    // 주별 타임라인과 동일한 색상 스타일 적용
    switch(props.classCount) {
      case 1: return '#D5E7FF'; // 1교시 색상
      case 2: return '#FFEED7'; // 2교시 분량 색상
      case 3: return '#DDF7E5'; // 3교시 분량 색상
      default: return '#D5E7FF';
    }
  }};
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ContentTitle = styled.span`
  color: ${props => props.isPast ? 'rgba(50, 50, 50, 0.5)' : 'black'};
  font-size: 16px;
  font-family: Pretendard;
  font-weight: 700;
  line-height: 24px;
  word-wrap: break-word;
  flex: 1;
`;

// --- Monthly Calendar Component ------------------------------------------------
export const MonthlyCalendar = ({ onDateSelect = () => {}, displayMonth, eduTimetableDetail }) => {
  const getDayContents = (date) => {
    if (!eduTimetableDetail || !eduTimetableDetail.dailyContents) return [];

    const dayKey = `day-${date.getDate().toString().padStart(2, '0')}`;
    const dayContent = eduTimetableDetail.dailyContents[dayKey];
    
    if (!dayContent) return [];

    // 1. 교시 순서대로 정렬
    const sortedPeriods = Object.keys(dayContent).sort((a, b) => {
      const getOrder = (key) => {
        if (key.startsWith("class")) return parseInt(key.replace("class", ""));
        switch (key) {
          case "morning": return 0;
          case "lunch": return 5;
          case "after-class": return 8;
          case "club": return 9;
          default: return 99;
        }
      };
      return getOrder(a) - getOrder(b);
    });

    if (sortedPeriods.length === 0) return [];

    // 2. 연속된 동일 콘텐츠 그룹화
    const groupedContents = [];
    let currentGroup = {
      contentId: dayContent[sortedPeriods[0]].contentId,
      contentTitle: dayContent[sortedPeriods[0]].contentTitle,
      periods: [sortedPeriods[0]],
    };

    for (let i = 1; i < sortedPeriods.length; i++) {
      const periodKey = sortedPeriods[i];
      const content = dayContent[periodKey];
      const prevPeriodKey = sortedPeriods[i-1];
      
      // 교시가 연속적인지 확인 (예: class1, class2)
      const isConsecutive = (prev, current) => {
        if (prev.startsWith("class") && current.startsWith("class")) {
          return parseInt(current.replace("class", "")) - parseInt(prev.replace("class", "")) === 1;
        }
        return false; // 교시 외에는 연속 처리 안함
      };

      if (content.contentId === currentGroup.contentId && isConsecutive(prevPeriodKey, periodKey)) {
        currentGroup.periods.push(periodKey);
      } else {
        groupedContents.push(currentGroup);
        currentGroup = {
          contentId: content.contentId,
          contentTitle: content.contentTitle,
          periods: [periodKey],
        };
      }
    }
    groupedContents.push(currentGroup);

    // 3. 그룹화된 데이터를 기반으로 최종 contents 배열 생성
    const contents = groupedContents.map(group => {
      let titlePrefix = "";
      if (group.periods.length > 1) {
        const startPeriod = group.periods[0].replace("class", "");
        const endPeriod = group.periods[group.periods.length - 1].replace("class", "");
        titlePrefix = `${startPeriod}~${endPeriod}교시`;
      } else {
        const periodKey = group.periods[0];
        if (periodKey.startsWith("class")) {
          titlePrefix = `${periodKey.replace("class", "")}교시`;
        } else {
           switch (periodKey) {
            case "morning": titlePrefix = "아침조례"; break;
            case "lunch": titlePrefix = "점심시간"; break;
            case "after-class": titlePrefix = "종례 시간"; break;
            case "club": titlePrefix = "동아리활동"; break;
            default: titlePrefix = "";
          }
        }
      }
      
      return {
        title: `${titlePrefix} ${group.contentTitle}`,
        classCount: group.periods.length,
      };
    });
    
    return contents;
  };
  
  const today = new Date();
  const currentDay = today.getDate();
  const currentMonth = today.getMonth();
  const currentYear = today.getFullYear();
  
  // displayMonth가 제공되면 사용, 아니면 현재 날짜 사용
  const targetDate = displayMonth || today;
  const year = targetDate.getFullYear();
  const month = targetDate.getMonth();

  // build dynamic weeks for Mon-Fri
  const lastDate = new Date(year, month + 1, 0).getDate();
  const weeks = [];
  let row = 0;
  weeks.push([null, null, null, null, null]);
  for (let day = 1; day <= lastDate; day++) {
    const dow = new Date(year, month, day).getDay(); // 0 Sun,1 Mon...
    if (dow >= 1 && dow <= 5) {
      if (dow === 1 && day !== 1) {
        row++;
        weeks.push([null, null, null, null, null]);
      }
      weeks[row][dow - 1] = day;
    }
  }

  return (
    <MonthlyCalendarContainer>
      <WeekdayHeader>
        {['월', '화', '수', '목', '금'].map((wd) => (
          <WeekArea><Weekday key={wd}>{wd}</Weekday></WeekArea>
        ))}
      </WeekdayHeader>
      {weeks.map((week, wi) => (
        <WeekRow key={wi}>
          {week.map((day, di) => {
            if (!day) {
              return <DayCell key={di} />;
            }
            // 과거 날짜 판단: 현재 월이 아니거나, 현재 월이지만 오늘보다 이전 날짜
            const isPast = year < currentYear || 
                          (year === currentYear && month < currentMonth) ||
                          (year === currentYear && month === currentMonth && day < currentDay);
            const isToday = year === currentYear && month === currentMonth && day === currentDay;
            const currentDate = new Date(year, month, day);
            const dayContents = getDayContents(currentDate);
            const hasContents = dayContents.length > 0;
            
            // 평일 모든 날짜를 클릭 가능하게 설정 (월~금)
            const dayOfWeek = currentDate.getDay();
            const isWeekday = dayOfWeek >= 1 && dayOfWeek <= 5; // 1=월요일, 5=금요일
            
            return (
              <DayCell 
                key={di} 
                clickable={isWeekday}
                onClick={() => isWeekday && onDateSelect(currentDate)}
              >
                {isPast ? (
                  <DisabledInner>
                    <DisabledNumber>{day}</DisabledNumber>
                  </DisabledInner>
                ) : isToday ? (
                  <ActiveInner>
                    <ActiveNumber>{day}</ActiveNumber>
                  </ActiveInner>
                ) : (
                  <DayNumber>{day}</DayNumber>
                )}
                
                {isWeekday && hasContents && (
                  <ContentList>
                    {dayContents.map((content, index) => (
                      <ContentItem 
                        key={index}
                        classCount={content.classCount}
                        isPast={isPast}
                      >
                        <ContentTitle isPast={isPast}>
                          {content.title}
                        </ContentTitle>
                      </ContentItem>
                    ))}
                  </ContentList>
                )}
              </DayCell>
            );
          })}
        </WeekRow>
      ))}
    </MonthlyCalendarContainer>
  );
};

const handleNavigateWeek = (direction) => {
  // 주 단위 이동 (추후 구현)
  console.log('주 이동:', direction);
};

// --- Main Molecule Component -----------------------------------------------------
const MoleculeContentTimeMonth = ({ 
  viewType = 'monthly', 
  onViewTypeChange = () => {},
  onDateSelect = () => {},
  contentList = [],
  eduTimetableDetail = null,
  monthOffset = 0,
  onMonthOffsetChange = () => {},
}) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [selectedViewType, setSelectedViewType] = useState(viewType);
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  // viewType prop이 변경되면 selectedViewType 업데이트
  useEffect(() => {
    setSelectedViewType(viewType);
  }, [viewType]);

  // 월별 이동 함수
  const handleNavigateMonth = (direction) => {
    const newOffset = direction === 'prev' ? monthOffset - 1 : monthOffset + 1;
    onMonthOffsetChange(newOffset);
  };

  // 현재 표시할 월 계산
  const getDisplayMonth = () => {
    const today = new Date();
    const displayDate = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1);
    return displayDate;
  };

  const handleAddContent = () => {
    // 콘텐츠 등록 팝업 열기 (추후 구현)
    setIsPopupOpen(true);
    console.log("콘텐츠 등록 팝업 열기");
  };

  const handleClosePopup = () => {
    setIsPopupOpen(false);
  }; 

  return (
    <TimelineContainer>
      <TimelineHeader>
        <HeaderLeft>
          <YearMonth>
            {getDisplayMonth().getFullYear()}년 {getDisplayMonth().getMonth() + 1}월
          </YearMonth>
          <NavigationControls>
            <TodayButton onClick={() => onMonthOffsetChange(0)}>
              <TodayText>Today</TodayText>
            </TodayButton>
            <ArrowButtons>
            <ArrowButton onClick={() => handleNavigateMonth('prev')}>
    {/* 왼쪽 화살표 */}
    <svg viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M18 6.43359L9.43433 15L18 23.5657"
            stroke="#AEAEB2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  </ArrowButton>

  <ArrowButton onClick={() => handleNavigateMonth('next')}>
    {/* 오른쪽 화살표 */}
    <svg viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 6.43359L20.5657 15L12 23.5657" 
            stroke="#AEAEB2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  </ArrowButton>
            </ArrowButtons>
          </NavigationControls>
        </HeaderLeft>
        <HeaderRight>
          <ViewTypeSelector>
            <ViewTypeButton
              active={selectedViewType === 'weekly'}
              onClick={() => {
                setSelectedViewType('weekly');
                onViewTypeChange('weekly');
              }}
            >주</ViewTypeButton>
            <ViewTypeButton
              active={selectedViewType === 'monthly'}
              onClick={() => {
                setSelectedViewType('monthly');
                onViewTypeChange('monthly');
              }}
            >월</ViewTypeButton>
          </ViewTypeSelector>
          <AddContentMainButton onClick={handleAddContent}>
            <svg
              width="25"
              height="25"
              viewBox="0 0 25 25"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                cx="12.4683"
                cy="12.3835"
                r="7.60791"
                stroke="#256EF4"
                strokeWidth="2"
              />
              <line
                x1="12.2383"
                y1="8.42432"
                x2="12.2383"
                y2="12.4626"
                stroke="#256EF4"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <line
                x1="14.2997"
                y1="13.9197"
                x2="12.2734"
                y2="12.4763"
                stroke="#256EF4"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
            콘텐츠 편성하기
          </AddContentMainButton>
        </HeaderRight>
      </TimelineHeader>

      <TimelineContent>
        <TimelineGrid>
          <DayColumn style={{ flex: 1, padding: 0, border: 'none' }}>
            <MonthlyCalendar onDateSelect={onDateSelect} displayMonth={getDisplayMonth()} eduTimetableDetail={eduTimetableDetail} />
          </DayColumn>
        </TimelineGrid>
      </TimelineContent>
      {isPopupOpen && (
        <ContentPopup
          onClose={handleClosePopup}
          contentList={contentList}
        />
      )}
    </TimelineContainer>
  );
};


const TimelineContainer = styled.div`
  width: 1240px;
  height: 2047px;
  background: white;
  display: flex;
  flex-direction: column;
`;

const TimelineHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0px 0px 20px 0px;
  border-bottom: 1px solid #D1D1D6;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 30px;
`;

const YearMonth = styled.span`
  color: #323232;
  font-size: 36px;
  font-family: Pretendard;
  font-weight: 700;
  line-height: 36px;
`;

const NavigationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 25px;
`;

const TodayButton = styled.div`
  height: 48px;
  padding: 13px 18px;
  background: white;
  border-radius: 8px;
  border: 1px solid #D1D1D6;
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const TodayText = styled.span`
  color: #323232;
  font-size: 17px;
  font-family: Poppins;
  font-weight: 400;
  line-height: 18px;
  text-align: center;
`;

const ArrowButtons = styled.div`
  display: flex;
  gap: 10px;
`;

const ArrowButton = styled.button`
  width: 36px;
  height: 36px;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: #f0f0f0;
  }
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const ViewTypeSelector = styled.div`
  height: 48px;
  padding: 4px;
  background: #EEF2F7;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 0;
`;

const ViewTypeButton = styled.button`
  height: 40px;
  min-width: 69px;
  padding: 4px 27px;
  background: ${props => props.active ? 'white' : 'transparent'};
  border: none;
  border-radius: 6px;
  cursor: pointer;
  color: #323232;
  font-size: 17px;
  font-family: Pretendard;
  font-weight: 400;
`;

const AddContentMainButton = styled.button`
  height: 48px;
  width: 100%;
  padding: 8px 12px;
  background: #F0F4FF;
  border: none;
  border-radius: 6px;
  color: white;
  font-size: 16px;
  color: #256EF4;
  font-family: Pretendard;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
`;

const TimelineContent = styled.div`
  flex: 1;
  overflow: hidden;
`;

const TimelineGrid = styled.div`
  display: flex;
  min-height: 1970px;
  position: relative;
`;

const TimeLabelsColumn = styled.div`
  width: 75px;
  height: 1970px;
  display: flex;
  flex-direction: column;
  border-right: 1px solid #D1D1D6;
  border-bottom: 1px solid #D1D1D6;
`;

const EmptyHeaderCell = styled.div`
  height: 50px;
  width: 75px;
  background: #F2F2F7;
  border-bottom: 1px solid #D1D1D6;
  border-right: 1px solid #D1D1D6;
`;



const TimeLabel = styled.div`
  height: 160px;
  width: 75px;
  padding: 15px 0px;
  color: #AEAEB2;
  font-size: 16px;
  font-family: Pretendard;
  font-weight: 700;
  line-height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DayColumn = styled.div`
  width: 233px;
  display: flex;
  flex-direction: column;
  border-right: 1px solid #D1D1D6;
`;



export default MoleculeContentTimeMonth;
